import { type } from 'arktype';

export const teamLocationTypeSchema = type("'primary' | 'secondary' | 'home_office'").configure({
  description:
    "Type of team-location assignment: 'primary' is the main work location, 'secondary' is a regular alternative site, and 'home_office' indicates remote/home working.",
});

export const teamLocationQuerySchema = type({
  'teamId?': type('string').configure({
    description: 'Filter assignments by team ID. Optional.',
    examples: ['tea_xyz789'],
  }),
  'locationId?': type('string').configure({
    description: 'Filter assignments by location ID. Optional.',
    examples: ['loc_hq01'],
  }),
  'type?': teamLocationTypeSchema,
  'startDate?': type('string').configure({
    description: 'Filter assignments whose validity starts on this ISO date (YYYY-MM-DD). Optional.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string').configure({
    description: 'Filter assignments whose validity ends on this ISO date (YYYY-MM-DD). Optional.',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Query parameters for filtering team-location assignments.' });

export type TeamLocationQuery = typeof teamLocationQuerySchema.t;

export const createTeamLocationSchema = type({
  teamId: type('string').configure({
    description: 'ID of the team being assigned to the location.',
    examples: ['tea_xyz789'],
  }),
  locationId: type('string').configure({
    description: 'ID of the location the team is being assigned to.',
    examples: ['loc_hq01'],
  }),
  type: teamLocationTypeSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment starts. Optional and nullable (open-ended start).',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment ends. Optional and nullable (open-ended end).',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Payload to assign a team to a location with an optional validity period.' });

export type CreateTeamLocation = typeof createTeamLocationSchema.t;

export const updateTeamLocationSchema = type({
  'type?': teamLocationTypeSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'Updated start date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'Updated end date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Partial update payload for an existing team-location assignment.' });

export type UpdateTeamLocation = typeof updateTeamLocationSchema.t;

export interface TeamLocationAssignmentResponse {
  id: string;
  teamId: string;
  locationId: string;
  type: 'primary' | 'secondary' | 'home_office';
  startDate: string | null;
  endDate: string | null;
  teamName: string;
  teamColor: string | null;
  teamIcon: string | null;
  createdAt: string;
  updatedAt: string;
}
